import React, { useCallback, useEffect, useRef } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import type { MalleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { RemixContainer } from '../remix';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { RemixOptionSelectionParameters } from '../remix/remix-overlay';

import type {
	DynamicUiSource,
	RemixContentComponentProps,
	RemixProductConfig,
	RemixProductIntegration,
} from './types';
import { filterRemixFragmentNodes, useRemixToAIFC } from './utils';

export type RemixContentComponentFullProps<
	TEditorApi extends PublicPluginAPI<MalleableUiPlugin> = PublicPluginAPI<MalleableUiPlugin>,
> = RemixContentComponentProps<TEditorApi> & {
	productConfig: RemixProductConfig;
	productIntegration: RemixProductIntegration;
};

const RemixContentComponentInner = <
	TEditorApi extends PublicPluginAPI<MalleableUiPlugin> = PublicPluginAPI<MalleableUiPlugin>,
>({
	handleModalClose,
	selectedContext,
	editorApi,
	onStartThinking,
	onStopThinking,
	remixModalMetadata,
	remixSource,
	productIntegration,
	productConfig,
	hiddenOptions,
}: RemixContentComponentFullProps<TEditorApi>) => {
	const isRemixCancelled = useRef(false);
	const experienceTracker = useAIMateExperienceTracker();
	const openRemixPreviewRef = useRef<() => void>(() => {});

	const { dynamicUiType } = useStagingAreaState();

	const onActionReady = useCallback(() => {
		if (!isRemixCancelled.current) {
			experienceTracker.succeed({ name: ExperienceName.REMIX_START });
			experienceTracker.start({
				name: ExperienceName.REMIX_RENDERS_IN_MODAL,
				id: ExperienceName.REMIX_RENDERS_IN_MODAL,
				attributes: {
					product: productConfig.analyticsProductName ?? productConfig.productKey,
					remixType: dynamicUiType,
					entryPoint: 'editor',
				},
			});
			openRemixPreviewRef.current();
		}
	}, [
		dynamicUiType,
		experienceTracker,
		productConfig.analyticsProductName,
		productConfig.productKey,
	]);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		remixStoreError,
		openRemixPreview,
		closeRemixPreview,
	} = useRemixToAIFC({
		config: productConfig,
		onActionReady,
		productIntegration,
	});
	openRemixPreviewRef.current = openRemixPreview;

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
		};
	}, []);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (active) {
				isRemixCancelled.current = false;
				onStartThinking();
				return;
			}

			isRemixCancelled.current = true;
			experienceTracker.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User cancelled remix thinking',
				attributes: { reason: 'User cancelled remix thinking' },
			});
			stopRemixing();
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'cancelledRemixThinking',
					originalRemixSource: remixSource,
					product: productConfig.analyticsProductName ?? productConfig.productKey,
				},
			});
			onStopThinking({ reopenModal: true });
		},
		[
			experienceTracker,
			onStartThinking,
			onStopThinking,
			productConfig.analyticsProductName,
			productConfig.productKey,
			remixSource,
			stopRemixing,
		],
	);

	const previousIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (!previousIsRemixError.current && isRemixError && !isRemixCancelled.current) {
			const errorMessage = remixStoreError?.content ?? 'Rovo agent error during remix';
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error(errorMessage),
				attributes: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
				},
			});
			onStopThinking({ reopenModal: true });
		}
		previousIsRemixError.current = isRemixError;
	}, [experienceTracker, isRemixError, onStopThinking, remixStoreError]);

	useEffect(() => {
		if (!isRemixCancelled.current && isRemixError) {
			if (productIntegration.isRemixPreviewVisible) {
				closeRemixPreview();
			}

			onStopThinking();
			productIntegration.toggleConversationAssistant?.('sidebar');
		}
	}, [closeRemixPreview, isRemixError, onStopThinking, productIntegration]);

	const constructDynamicUiSource = useCallback(async (): Promise<DynamicUiSource | undefined> => {
		const localIds = selectedContext?.localIds ?? [];
		const startLocalId = localIds[0] ?? '';
		const endLocalId = localIds[localIds.length - 1] ?? '';
		const fragmentAdf = filterRemixFragmentNodes(selectedContext?.selectionFragment ?? null);

		let fullAdf: DocNode = { type: 'doc', version: 1, content: [] };
		if (editorApi) {
			fullAdf = await new Promise((resolve) => {
				try {
					editorApi.core?.actions?.requestDocument?.(
						(doc?: JSONDocNode) => {
							resolve((doc as DocNode | undefined) ?? { type: 'doc', version: 1, content: [] });
						},
						{ alwaysFire: true },
					);
				} catch {
					resolve({ type: 'doc', version: 1, content: [] });
				}
			});
		}

		return {
			contentType: productIntegration.contentType,
			startLocalId,
			endLocalId,
			startIndex: selectedContext?.startIndex ?? 0,
			endIndex: selectedContext?.endIndex ?? -1,
			fragmentAdf,
			fullAdf,
		};
	}, [editorApi, productIntegration.contentType, selectedContext]);

	const onOptionSelect = useCallback(
		async ({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { remixType: type },
			});
			const dynamicUiSource = await constructDynamicUiSource();
			void sendAIFCPrompt({
				userPrompt,
				dynamicUiType: type,
				dynamicUiSubtype: subType,
				dynamicUiSource,
				interactionSource: remixModalMetadata?.interactionSource,
			});
		},
		[constructDynamicUiSource, experienceTracker, remixModalMetadata, sendAIFCPrompt],
	);

	return (
		<RemixContainer
			onClose={handleModalClose}
			onOptionSelect={onOptionSelect}
			onRemixFlowActiveChange={onRemixFlowActiveChange}
			isEphemeralViewVisible={productIntegration.isRemixPreviewVisible}
			hiddenOptions={hiddenOptions}
		/>
	);
};

export const RemixContentComponent = <
	TEditorApi extends PublicPluginAPI<MalleableUiPlugin> = PublicPluginAPI<MalleableUiPlugin>,
>(
	props: RemixContentComponentFullProps<TEditorApi>,
): React.JSX.Element | null => {
	// `startRemixThinking` closes the options modal (`isRemixModalOpen` false) while remix
	// is still active. We must keep rendering `RemixContainer` (thinking bar / ephemeral)
	// whenever the editor reports `isThinking`, not only when the modal is open.
	if (!(props.isModalOpen || props.isThinking)) {
		return null;
	}

	return (
		<RemixContentComponentInner
			handleModalClose={props.handleModalClose}
			isModalOpen={props.isModalOpen}
			selectedContext={props.selectedContext}
			editorApi={props.editorApi}
			onStartThinking={props.onStartThinking}
			onStopThinking={props.onStopThinking}
			remixModalMetadata={props.remixModalMetadata}
			remixSource={props.remixSource}
			isThinking={props.isThinking}
			productIntegration={props.productIntegration}
			productConfig={props.productConfig}
			hiddenOptions={props.hiddenOptions}
		/>
	);
};
